import { useState, useRef, useEffect } from 'react';
import * as yup from 'yup';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { AvailableServiceTimeSlots } from '../../../data/entities/booking/AvailableServiceTimeSlots';
import { BookingModel } from '../../../data/entities/booking/BookingModel';
import { sharedProperty, useAsync } from '../../../hooks';
import { formatTimeOnly } from '../../../utils/dateUtils';
import BookingService from '../../../services/booking/BookingService';
import { BookingEventModel } from '../../../data/entities/booking/BookingEventModel';

export const useBookingSchedule = (props: Readonly<{ booking: sharedProperty<BookingModel>, locationId: number}>) => {

  const bookingService = new BookingService();

  const validationSchema = yup.object().shape({
    date: yup.date().required('Please select a date'),
    events: yup.array().of(
      yup.object().shape({
        startTime: yup.string().required('Please select a time'),
      })
    ),
  });

  const { value: availableDates, error, loading } = useAsync(() => {
    const serviceProviderPairs = props.booking.value.bookingEvents.reduce((acc, event: BookingEventModel) => {
      if (event.serviceId !== undefined) {
        acc[event.serviceId] = event.providerId ?? -1;
      }
      return acc;
    }, {} as Record<number, number>);

    if (Object.keys(serviceProviderPairs).length === 0) {
      return Promise.resolve([]);
    }

    return bookingService.getAvailableServiceDays(props.locationId, new Date(), new Date(new Date().setMonth(new Date().getMonth() + 2)), serviceProviderPairs);
  }, [props.booking]);

  const modalBodyRef = useRef<HTMLDivElement>(null);
  
  const [selectedTimes, setSelectedTimes] = useState<{ 
    [key: string]: { 
      startTime: Date, 
      serviceDuration: number,
      bufferDuration: number,
      totalDuration: number,
      serviceId: number,
      providerId: number | null,
      isAnyProvider: boolean,
      availableProviderIds: number[],
      index: number
    } 
  }>({});

  const getEventKey = (serviceId: number, providerId: number | null, index: number) => {
    return `${index}-${serviceId}-${providerId || 'null'}`;
  };

  // Sync selectedTimes with current booking events
  useEffect(() => {
    setSelectedTimes(prev => {
      const syncedTimes: typeof selectedTimes = {};
      
      props.booking.value.bookingEvents.forEach((event, index) => {
        if (event.startTime && event.serviceId) {
          const key = getEventKey(event.serviceId, event.providerId || null, index);
          
          // Keep availableProviderIds from the previous selection if it exists
          const existingEntry = prev[key];

          const startDate = new Date(props.booking.value.date || new Date());
          const timeParts = event.startTime.match(/(\d+):(\d+)(?::(\d+))?\s*(AM|PM)?/i);
          
          if (timeParts) {
            let hours = parseInt(timeParts[1]);
            const minutes = parseInt(timeParts[2]);
            const period = timeParts[4];
            
            if (period) {
              if (period.toUpperCase() === 'PM' && hours !== 12) {
                hours += 12;
              } else if (period.toUpperCase() === 'AM' && hours === 12) {
                hours = 0;
              }
            }
            
            startDate.setHours(hours, minutes, 0, 0);

            const totalDuration = event.duration || event.service?.defaultDuration || 0;
            const serviceDuration = event.service?.defaultDuration || totalDuration;
            const bufferDuration = totalDuration - serviceDuration;
            
            syncedTimes[key] = {
              startTime: startDate,
              serviceDuration: existingEntry?.serviceDuration ?? serviceDuration,
              bufferDuration: existingEntry?.bufferDuration ?? (bufferDuration >= 0 ? bufferDuration : 0),
              totalDuration: existingEntry?.totalDuration ?? totalDuration,
              serviceId: event.serviceId,
              providerId: event.providerId || null,
              isAnyProvider: event.isAnyProvider || event.providerId === -1,
              availableProviderIds: existingEntry?.availableProviderIds || [],
              index: index
            };
            
          } 
        }
      });
      return syncedTimes;
    });
  }, [props.booking.value.bookingEvents]);


  const onDateChange = (event: DatePickerChangeEvent, setFieldValue: any, setTouched:any, setErrors: any) => {
    const newDate = event.target.value
      ? new Date(event.target.value.toString())
      : new Date();
    props.booking.setter({
      ...props.booking.value,
      date: newDate,
      bookingEvents: props.booking.value.bookingEvents.map((bookingEvent) => ({
        ...bookingEvent,
        date: newDate,
        startTime: null, // Reset the startTime when the date changes
        endTime: null
      })),
    });
    setFieldValue("date", newDate);
    setFieldValue("events", props.booking.value.bookingEvents.map(() => ({
      startTime: null,
      endTime: null
    })));
    setErrors({});
    setTouched({});
    
    // Clear selected times when date changes
    setSelectedTimes({});
  };

  const onTimeSelect = (time: string, eventId: number, index: number, setFieldValue: any, timeSlot: AvailableServiceTimeSlots) => {
    const selectedTime = timeSlot.serviceStart;
    const totalDuration = timeSlot.totalDuration || 0;
    const endTime = timeSlot.serviceEnd;

    const formattedStartTime = formatTimeOnly(selectedTime);
    const formattedEndTime = formatTimeOnly(endTime);

    setFieldValue(`events[${index}].startTime`, formattedStartTime);
    setFieldValue(`events[${index}].endTime`, formattedEndTime);

    const currentEvent = props.booking.value.bookingEvents[index];

    props.booking.setter({
      ...props.booking.value,
      bookingEvents: props.booking.value.bookingEvents.map((e, i) => {
        if (i === index)
          return {
            ...e,
            startTime: formattedStartTime,
            endTime: formattedEndTime,
            duration: totalDuration,
            providerId: e.isAnyProvider ? -1 : timeSlot.providerId // -1 for the api to recognize as any provider 
          };
        else return e;
      }),
    });

const key = getEventKey(
  currentEvent.serviceId!, 
  currentEvent.isAnyProvider ? -1 : timeSlot.providerId, 
  index
);

    setSelectedTimes(prev => ({
      ...prev,
      [key]: { 
        startTime: selectedTime, 
        serviceDuration: timeSlot.serviceDuration,
        bufferDuration: timeSlot.bufferDuration,
        totalDuration,
        serviceId: currentEvent.serviceId!,
        providerId: timeSlot.providerId,
        isAnyProvider: currentEvent.isAnyProvider || false,
        availableProviderIds: timeSlot.availableProviderIds || [],
        index: index
      }
    }));
  };


  const canUseDifferentProviders = (
    selectedProviderId: number | null,
    selectedIsAnyProvider: boolean,
    selectedAvailableProviderIds: number[],
    candidateProviderId: number,
    candidateAvailableProviderIds: number[]
  ): boolean => {
    const selectedIsAny = selectedIsAnyProvider || selectedProviderId === -1 || selectedProviderId === null;
    const candidateIsAny = candidateProviderId === -1;

    // Both have specific providers 
    if (!selectedIsAny && !candidateIsAny) {
      return selectedProviderId !== candidateProviderId;
    }

    // Selected is "any provider" check if there's someone available besides the candidate's provider
    if (selectedIsAny && !candidateIsAny) {
      if (selectedAvailableProviderIds.length > 0) {
        return selectedAvailableProviderIds.some(id => id !== candidateProviderId);
      }
      return false; // No provider info, be conservative
    }

    // Candidate is "any provider" check if there's someone available besides the selected's provider
    if (!selectedIsAny && candidateIsAny) {
      if (candidateAvailableProviderIds.length > 0) {
        return candidateAvailableProviderIds.some(id => id !== selectedProviderId);
      }
      return false;
    }

    // Both are "any provider" check if at least 2 distinct providers exist across both
    if (selectedIsAny && candidateIsAny) {
      const allProviders = new Set([...selectedAvailableProviderIds, ...candidateAvailableProviderIds]);
      return allProviders.size >= 2;
    }

    return false;
  };

  const isTimeSlotDisabled = (timeSlot: AvailableServiceTimeSlots, currentEventId: number, currentServiceId?: number, currentProviderId?: number | null, currentIndex?: number) => {
    const candidateStart = new Date(timeSlot.serviceStart).getTime();
    const candidateServiceEnd = candidateStart + timeSlot.serviceDuration * 60000;
    const candidateTotalEnd = candidateStart + timeSlot.totalDuration * 60000;

    // Get the current event key to exclude it from collision detection
    const currentKey = (currentServiceId !== undefined && currentIndex !== undefined) 
      ? getEventKey(currentServiceId, currentProviderId || null, currentIndex) 
      : null;

    const isDisabled = Object.entries(selectedTimes).some(([key, selected]) => {
      if (key === currentKey) {
        return false;
      }

      const selectedStart = new Date(selected.startTime).getTime();
      const selectedServiceEnd = selectedStart + selected.serviceDuration * 60000;
      const selectedTotalEnd = selectedStart + selected.totalDuration * 60000;

      // Allow if no overlap at all (including buffers)
      const totalOverlap = candidateStart < selectedTotalEnd && candidateTotalEnd > selectedStart;
      if (!totalOverlap) return false;

      // Always block if Service time overlap (customer can't be in two places at once)
      const serviceOverlap = candidateStart < selectedServiceEnd && candidateServiceEnd > selectedStart;
      if (serviceOverlap) {
        return true;
      }

      // check if different providers can handle overlap 
      const differentProviderPossible = canUseDifferentProviders(
        selected.providerId,
        selected.isAnyProvider,
        selected.availableProviderIds,
        timeSlot.providerId,
        timeSlot.availableProviderIds || []
      );

      // If a different provider can be used, the buffer doesn't matter for the customer
      return !differentProviderPossible;
    });

    return isDisabled;
  };

  return {
    modalBodyRef,
    validationSchema,
    onDateChange,
    onTimeSelect,
    isTimeSlotDisabled,
    availableDates,
    error,
    loading,
    selectedTimes,
  };
};